import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { useTypingIndicatorSubscription } from '../../hooks/useTypingIndicator';
import { useAuth } from '../../contexts/AuthContext';

interface TypingIndicatorProps {
  conversationId: string;
}

/**
 * TypingIndicator Component
 *
 * Displays "User is typing..." indicator when other users are typing
 * - Real-time subscription to typing status
 * - Filters out current user
 * - Animated dots
 */
export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ conversationId }) => {
  const { authState } = useAuth();
  const user = authState.user;
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);

  useTypingIndicatorSubscription(conversationId, (userIds) => {
    // Filter out current user
    const otherUsersTyping = userIds.filter((id) => id !== user?.uid);
    setTypingUserIds(otherUsersTyping);
  });

  if (typingUserIds.length === 0) {
    return null;
  }

  const getTypingText = (): string => {
    if (typingUserIds.length === 1) {
      return 'is typing';
    } else if (typingUserIds.length === 2) {
      return 'are typing';
    } else {
      return `${typingUserIds.length} people are typing`;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
      }}
    >
      {/* Show avatar for single user typing */}
      {typingUserIds.length === 1 && (
        <Avatar sx={{ width: 24, height: 24 }}>
          {typingUserIds[0][0].toUpperCase()}
        </Avatar>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {getTypingText()}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          <TypingDot delay={0} />
          <TypingDot delay={0.2} />
          <TypingDot delay={0.4} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Animated typing dot
 */
const TypingDot: React.FC<{ delay: number }> = ({ delay }) => {
  return (
    <Box
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        bgcolor: 'text.secondary',
        animation: 'typingDot 1.4s infinite',
        animationDelay: `${delay}s`,
        '@keyframes typingDot': {
          '0%, 60%, 100%': {
            opacity: 0.3,
          },
          '30%': {
            opacity: 1,
          },
        },
      }}
    />
  );
};
